import { FONT_LINK, SHEET_CSS } from './sheetStyles';

const MARGIN_MM = 12;
const PAGE_W_MM = 210;
const PAGE_H_MM = 297;
const CONTENT_W_MM = PAGE_W_MM - MARGIN_MM * 2; // 186
const CONTENT_H_MM = PAGE_H_MM - MARGIN_MM * 2; // 273

// Load the Devanagari-capable web font (falls back to system fonts if offline).
export const ensureFonts = async () => {
  if (!document.getElementById('aip-fonts')) {
    const link = document.createElement('link');
    link.id = 'aip-fonts';
    link.rel = 'stylesheet';
    link.href = FONT_LINK;
    document.head.appendChild(link);
  }
  try {
    await Promise.race([
      Promise.all([
        document.fonts.load("700 16px 'Noto Serif Devanagari'", 'अआइ'),
        document.fonts.load("400 16px 'Noto Serif Devanagari'", 'अआइ'),
        document.fonts.load("400 16px 'Noto Serif'", 'Abc')
      ]),
      new Promise((r) => setTimeout(r, 4000))
    ]);
    await document.fonts.ready;
  } catch { /* fall back to system fonts */ }
};

// Compute page cut positions (CSS px) so no .aip-block is split across pages.
export const computeCuts = (blocks, totalH, pageH) => {
  const cuts = [0];
  let start = 0;
  while (totalH - start > pageH + 0.5) {
    const limit = start + pageH;
    const minTop = start + 40; // avoid near-empty pages when a block is taller than a page
    let cut = limit;
    for (const b of blocks) {
      if (b.top < limit && b.bottom > limit && b.top > minTop && b.top < cut) cut = b.top;
    }
    cuts.push(cut);
    start = cut;
  }
  cuts.push(totalH);
  return cuts;
};

export const downloadSheetPdf = async (sheetEl, fileName) => {
  await ensureFonts();
  const html2canvas = (await import('html2canvas')).default;
  const { jsPDF } = await import('jspdf');

  // Render a clone off-screen so on-screen scaling/scroll never affects output.
  const host = document.createElement('div');
  host.style.cssText = 'position:fixed;left:-10000px;top:0;background:#fff;';
  const clone = sheetEl.cloneNode(true);
  host.appendChild(clone);
  document.body.appendChild(host);
  try {
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    const imgs = Array.from(clone.querySelectorAll('img'));
    await Promise.all(imgs.map((im) => (im.complete ? null : new Promise((r) => { im.onload = im.onerror = r; }))));

    const SCALE = 2;
    const width = clone.offsetWidth;
    const totalH = clone.scrollHeight;
    const pxPerMm = width / CONTENT_W_MM;
    const pageH = CONTENT_H_MM * pxPerMm;
    const origin = clone.getBoundingClientRect().top;
    const blocks = Array.from(clone.querySelectorAll('.aip-block')).map((el) => {
      const r = el.getBoundingClientRect();
      return { top: r.top - origin, bottom: r.bottom - origin };
    });
    const cuts = computeCuts(blocks, totalH, pageH);

    const canvas = await html2canvas(clone, {
      scale: SCALE, useCORS: true, allowTaint: false, backgroundColor: '#ffffff', logging: false,
      width, height: totalH, windowWidth: width
    });
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    for (let i = 0; i < cuts.length - 1; i++) {
      const y0 = Math.round(cuts[i] * SCALE);
      const h = Math.max(1, Math.round((cuts[i + 1] - cuts[i]) * SCALE));
      const slice = document.createElement('canvas');
      slice.width = canvas.width;
      slice.height = h;
      const ctx = slice.getContext('2d');
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, slice.width, slice.height);
      ctx.drawImage(canvas, 0, y0, canvas.width, h, 0, 0, canvas.width, h);
      if (i > 0) pdf.addPage();
      pdf.addImage(slice.toDataURL('image/jpeg', 0.95), 'JPEG', MARGIN_MM, MARGIN_MM, CONTENT_W_MM, (h / SCALE) / pxPerMm);
    }
    pdf.save(fileName);
  } finally {
    document.body.removeChild(host);
  }
};

// Native print: vector text, razor sharp, @page A4 portrait with 12mm margin.
export const printSheet = async (sheetEl, title) => {
  await ensureFonts();
  const win = window.open('', '_blank', 'width=900,height=1000');
  if (!win) throw new Error('Pop-up blocked. Allow pop-ups to print.');
  win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title}</title>
<link rel="stylesheet" href="${FONT_LINK}">
<style>${SHEET_CSS}
@media print { html, body { margin: 0; padding: 0; } .aip-sheet { width: 100% !important; } }
body { margin: 0; background: #fff; }</style></head><body>${sheetEl.outerHTML}</body></html>`);
  win.document.close();
  const go = () => { win.focus(); win.print(); };
  if (win.document.fonts && win.document.fonts.ready) win.document.fonts.ready.then(() => setTimeout(go, 300));
  else setTimeout(go, 1200);
};

export const safeFileName = (s) => (s || 'paper').replace(/[^\w-]+/g, '_').replace(/_+/g, '_');
